import type { Route } from '../../types'
import type { ShopLocale } from '../../../i18n/shopLocale'
import { normalizePathname } from '../../routing'
import { normalizeSlugPath } from '../../seoHelpers'
import { DEFAULT_DESCRIPTION } from '../constants'
import { buildDefaultSeoAlternates } from '../localePolicy'
import type { ShopSeoData } from '../types'

export const buildDefaultSeo = (siteName: string): ShopSeoData => ({
  title: siteName,
  description: DEFAULT_DESCRIPTION,
  canonicalPath: '/',
  alternates: buildDefaultSeoAlternates('/'),
  ogType: 'website',
  pageType: 'home',
  entityName: siteName,
  breadcrumbs: [{ name: 'Home', path: '/' }],
})

export const buildErrorSeo = ({
  code,
  currentPathname,
  siteName,
}: {
  code: number
  currentPathname?: string
  siteName: string
}): ShopSeoData => {
  const canonicalPath = normalizePathname(currentPathname || `/error/${code}`)
  return {
    title: `Errore ${code} | ${siteName}`,
    description: 'Si e verificato un errore durante il caricamento della pagina.',
    canonicalPath,
    alternates: buildDefaultSeoAlternates(canonicalPath),
    noindex: true,
    ogType: 'website',
    pageType: 'error',
    entityName: `Errore ${code}`,
    breadcrumbs: [{ name: 'Home', path: '/' }],
  }
}

const resolveFallbackPath = (route: Route, currentPathname?: string): string => {
  if (route.mode === 'page') return normalizeSlugPath(route.slug)
  if (route.mode === 'product') {
    return normalizePathname(currentPathname || `/products/${encodeURIComponent(route.slug)}`)
  }
  return '/'
}

export const buildFallbackSeo = ({
  route,
  currentPathname,
  siteName,
  activeLocale,
}: {
  route: Route
  currentPathname?: string
  siteName: string
  activeLocale?: ShopLocale
}): ShopSeoData => {
  const fallbackPath = resolveFallbackPath(route, currentPathname)

  return {
    title: siteName,
    description: DEFAULT_DESCRIPTION,
    canonicalPath: fallbackPath,
    ...(activeLocale ? { canonicalLocale: activeLocale } : {}),
    alternates: buildDefaultSeoAlternates(fallbackPath),
    noindex: route.mode === 'preview',
    ogType: 'website',
    pageType: 'page',
    entityName: siteName,
    breadcrumbs: [{ name: 'Home', path: '/' }],
  }
}
